import { useMemo, useState, useEffect, ChangeEvent } from 'react';
import { useSelector } from 'react-redux';

import { convertUserInputToDuration, formatInputDuration } from 'src/shared/utils';
import { getDurationFormat } from 'src/store/user/user.selectors';

export const useEditingDurationInput = (
  incomingDuration: number | undefined,
  onBlur?: (newDuration: number | undefined) => void,
  isNegativeAllowed?: boolean,
  max?: number | null,
) => {
  const durationFormat = useSelector(getDurationFormat);

  // Format incoming date time value to user formatted time value (e.g. 2023-04-25T09:30:00 to 9:00 am)
  const incomingDurationValue = useMemo(() => {
    if (incomingDuration === 0) {
      return '00:00';
    }

    return incomingDuration ? formatInputDuration(incomingDuration, durationFormat, 'seconds') : '';
  }, [incomingDuration]);

  // Editing state to be used for text input
  const [editingDurationValue, setEditingDurationValue] = useState(incomingDurationValue);

  // Reset editing state whenever incomingDuration changes
  useEffect(() => {
    setEditingDurationValue(incomingDurationValue);
  }, [incomingDurationValue]);

  // Update editing state
  const handleEditingDurationValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEditingDurationValue(event.target.value);
  };

  // Convert user input state to formatted time and pass to onBlur callback function
  const handleEditingDurationBlur = () => {
    if (!editingDurationValue) {
      return onBlur?.(undefined);
    }

    const convertedDuration = convertUserInputToDuration(editingDurationValue, isNegativeAllowed, max);

    // Revert if input value is invalid
    if (!convertedDuration.toString().trim().length) {
      setEditingDurationValue(incomingDurationValue);
      return;
    }

    // Trigger state change if new value is the same as previous value  (needed to trigger formatting useMemo)
    if (convertedDuration === incomingDuration) {
      setEditingDurationValue(incomingDurationValue);
      return;
    }

    onBlur?.(+convertedDuration);
  };

  return {
    value: editingDurationValue,
    onInputChange: handleEditingDurationValueChange,
    onInputBlur: handleEditingDurationBlur,
  };
};
